/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
declare global {
  interface Window {
    google: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private document = inject(DOCUMENT);
  private isApiLoaded = false;
  // One script load shared by every caller, so a second call made while the
  // script is still loading doesn't append another <script> tag.
  private load$: Observable<any> | null = null;

  loadGoogleMapsApi(apiKey: string, libraries: string[] = ['places'], ...rest: string[]): Observable<any> {
    if (!this.load$) {
      this.load$ = this.createLoad(apiKey, libraries, rest).pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.load$;
  }

  private createLoad(apiKey: string, libraries: string[], rest: string[]): Observable<any> {
    return new Observable((observer) => {
      const win = this.document.defaultView as Window | null;
      // If the API is already loaded, return the Google object immediately
      if (this.isApiLoaded || win?.google?.maps) {
        this.isApiLoaded = true;
        observer.next(win?.google);
        observer.complete();
        return;
      }

      // Prepare the libraries parameter
      const librariesParam = libraries.join(',');

      // Prepare the additional query parameters (rest)
      const restParams = rest.length > 0 ? `&${rest.join('&')}` : '';

      // Create the script element to load the Google Maps API
      const script = this.document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${librariesParam}${restParams}`;
      script.async = true;
      script.defer = true;

      // On successful loading of the script, notify the observer with the Google object
      script.onload = () => {
        this.isApiLoaded = true;
        observer.next(window.google);
        observer.complete();
      };

      // If the script fails to load, notify the observer with an error and
      // allow a later call to try again.
      script.onerror = () => {
        this.load$ = null;
        script.remove();
        observer.error('Failed to load Google Maps API');
      };

      // Append the script to the document body
      this.document.body.appendChild(script);
    });
  }
}
